import { Injectable } from '@angular/core'
import { NgbModal } from '@ng-bootstrap/ng-bootstrap'
import { TranslateService } from '@ngx-translate/core'
import { APIService } from 'api/module'
import { LicenseService } from 'common/util/services/license.service'
import { TapeFile } from 'elements-sdk'
import { RestoreAddModalComponent } from 'ltfs/module'
import { TapeRestorerService } from 'ltfs/services/tapeRestorer.service'
import { IdentityService } from 'main/services/identity.service'
import { MediaPermissionsService } from 'media/services/mediaPermissions.service'
import { PanelService } from 'panel/services/panel.service'
import { MediaOperationsService } from '../services/mediaOperations.service'
import { NotifyService } from 'common/util/services/notify.service'
import { BaseOperation, MediaOperationInput } from './base'

@Injectable({ providedIn: 'root' })
class RestoreOp extends BaseOperation {
  icon = 'far fa-history'
  showInSelectionOverflowMenu = true
  supportsMultiple = true
  supportsDirs = true
  supportsBundles = true
  weight = 91

  constructor (
    private translate: TranslateService,
    private mediaPermissions: MediaPermissionsService,
    private api: APIService,
    private tapeRestorer: TapeRestorerService,
    private panel: PanelService,
    private license: LicenseService,
    private identity: IdentityService,
    private notify: NotifyService,
    private ngbModal: NgbModal,
  ) {
    super()
    this.label = translate.instant('Restore')
  }

  isVisibleFor ({ dirs, bundles }, context) {
    return !this.panel.active && this.license.hasComponent('ltfs') && this.identity.hasPermission('ltfs:restore') && super.isVisibleFor({ dirs, bundles }, context)
  }

  isVisibleForDir (dir) {
    return this.mediaPermissions.getPermissionForFile(dir)?.allowWriteFs ?? false
  }

  isVisibleForBundle (bundle) {
    return bundle.mainfile && this.mediaPermissions.getPermissionForBundle(bundle)?.allowWriteFs
  }

  isEnabledForBundle (bundle) {
    return bundle.asset?.hasBackups
  }

  async run ({ dirs, bundles }: MediaOperationInput) {
    let dirEntries = await Promise.all(dirs.map(async dir => {
      const fullpath = dir.fullPath
      const entries = await this.api.tapeArchive.getAllArchivedFileEntries({ fullpath })
      return entries[0] as TapeFile | null
    }))
    dirEntries = dirEntries.filter(x => !!x)
    if (dirEntries.length > 0) {
      const m = this.ngbModal.open(RestoreAddModalComponent)
      m.componentInstance.entries = dirEntries
      m.result.catch(() => null)
    }

    const qs: Promise<boolean>[] = []
    for (const bundle of bundles) {
      if (bundle.asset?.hasBackups) {
        qs.push(
          this.api.mediaLibrary.getAllAssetTapeBackups({ asset: bundle.asset.id }).then(backups => {
            if (!backups[0]) {
              return false
            }
            this.tapeRestorer.enqueue([backups[0].tapeFile], false)
            return true
          }),
        )
      } else {
        this.notify.warning(this.translate.instant('%s is not archived').replace('%s', bundle.name))
      }
    }

    if (qs.length > 0) {
      const queued = await Promise.all(qs)
      const count = queued.filter(x => !!x).length
      if (count) {
        this.notify.info(this.translate.instant('%s files enqueued for restore').replace('%s', `${count}`))
      }
    }
  }
}

MediaOperationsService.register(RestoreOp)
